import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import {
  Icon,
  IconButton,
  IconName,
  IconSize,
  Input,
  ProColors,
} from '../../aura';
import { FormContextData, useFormContext } from '../../state';
import { PasswordRule } from '../../api-client/get-password-rules';
import { FoundationColors } from '../../theme';
import { useValidator } from './helpers/use-validation';
import { useBlur } from './helpers/use-blur';
import {
  lengthFromPattern,
  matchesRule,
  usePasswordRules,
} from './helpers/use-password-rules';

export function Password(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'password' });
  const { t: tRule } = useTranslation('input');
  const {
    data: { password },
    setData,
  } = useFormContext();
  const [visible, setVisible] = useState(false);
  const rules = usePasswordRules();

  const validate = useCallback(
    (data: FormContextData) => {
      if (!data.password.length) {
        return 'Password is empty';
      }
      const failing = rules.filter(rule => !matchesRule(rule, data.password));
      // The returned string only needs to be non-null to block the step; the checklist shows the
      // per-rule detail, so the stable rule name is enough here.
      return failing.length ? failing[0].ruleName : null;
    },
    [rules],
  );
  const wasValidated = useValidator(validate);
  const { wasBlurred, onBlur } = useBlur();
  const showErrors = wasValidated || wasBlurred;
  const fieldHasError =
    showErrors && rules.some(rule => !matchesRule(rule, password));

  const onValueChange = useCallback(
    (value: string) => {
      setData(current => ({ ...current, password: value }));
    },
    [setData],
  );

  const onToggleVisibility = useCallback(() => {
    setVisible(v => !v);
  }, []);

  const visibleRules = rules.filter(
    rule => !(rule.hideWhenValid && matchesRule(rule, password)),
  );

  // Localise the rule copy via its Maia CMS key (keyed under the `input` namespace). i18next falls
  // back to the bundled English (`fallbackLng: 'en'`) when the active language has no entry.
  const ruleLabel = (rule: PasswordRule) =>
    tRule(rule.maiaCmsKey, {
      length: lengthFromPattern(rule.pattern),
    });

  return (
    <>
      <Input
        id="password"
        type={visible ? 'text' : 'password'}
        label={t('label')}
        value={password}
        onValueChange={onValueChange}
        onBlur={onBlur}
        // Pair the error flavor with a blank-space subText (as useFormErrors does) so the field
        // reliably enters Aura's has-error state; the checklist below carries the actual messages.
        subText={fieldHasError ? ' ' : undefined}
        subTextFlavor={fieldHasError ? 'error' : undefined}
        innerRightComponent={
          <IconButton
            name={visible ? IconName.TW_PRO_HIDE : IconName.TW_PRO_SHOW}
            size={IconSize.SMALL}
            color={FoundationColors.blue[600]}
            flavor="tertiary"
            onClick={onToggleVisibility}
            css={css({ marginRight: -8 })}
          />
        }
      />
      {visibleRules.length > 0 && (
        <div
          css={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            marginTop: 12,
          })}
        >
          {visibleRules.map(rule => {
            const satisfied = matchesRule(rule, password);
            const invalid = !satisfied && showErrors;
            const iconColor = satisfied
              ? ProColors.teal[600]
              : invalid
                ? ProColors.coral[600]
                : ProColors.navy[300];
            return (
              <div
                key={rule.ruleName}
                className="aura--copy-xs"
                css={css({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: invalid ? ProColors.coral[600] : ProColors.navy[500],
                })}
              >
                <Icon
                  name={
                    satisfied
                      ? IconName.TW_PRO_CHECK_FILLED
                      : invalid
                        ? IconName.TW_PRO_CLOSE
                        : IconName.TW_PRO_CHECK_FILLED
                  }
                  size={IconSize.SMALL}
                  color={iconColor}
                />
                {ruleLabel(rule)}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
